import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsLogged, clearUser } from '../store/reducer/appReducer';
import { healthCheckService, HealthCheckResult } from '../services/healthCheck.service';
import ErrorPage from '../pages/ErrorPage/ErrorPage';
import Loading from './Loading';

interface AppWrapperProps {
  children: React.ReactNode;
}

export default function AppWrapper({ children }: AppWrapperProps) {
  const dispatch = useDispatch();
  const loggedIn = useSelector(selectIsLogged);
  const [healthCheck, setHealthCheck] = useState<{
    isChecking: boolean;
    result?: HealthCheckResult;
  }>({
    isChecking: true
  });

  useEffect(() => {
    // Only check health if user is logged in
    if (loggedIn) {
      checkHealth();
    } else {
      setHealthCheck({ isChecking: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const checkHealth = async () => {
    try {
      setHealthCheck({ isChecking: true });
      
      const result = await healthCheckService.checkConnection();
      
      if (!result.isConnected) {
        // Handle different error types
        if (result.error?.type === 'AUTH_ERROR') {
          // Clear user data and let App.tsx handle redirect
          dispatch(clearUser());
          setHealthCheck({ isChecking: false, result });
          return;
        }
        
        // For other errors, show error page
        setHealthCheck({ isChecking: false, result });
      } else {
        // Connection is good
        setHealthCheck({ isChecking: false, result });
      }
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthCheck({ 
        isChecking: false, 
        result: {
          isConnected: false,
          error: {
            type: 'UNKNOWN_ERROR',
            message: 'ไม่สามารถตรวจสอบการเชื่อมต่อได้'
          }
        }
      });
    }
  };

  // Show loading while checking
  if (healthCheck.isChecking) {
    return <Loading loading={true} />;
  }

  // Show error page if connection failed (except auth errors which redirect)
  if (loggedIn && healthCheck.result && !healthCheck.result.isConnected) {
    const { error } = healthCheck.result;
    return (
      <ErrorPage
        error={error}
        onRetry={() => checkHealth()}
        onGoHome={() => window.location.href = '/'}
      />
    );
  }

  // Normal rendering
  return <>{children}</>;
}
